import type { ModalContentNavigationAction } from '../content/types';
import { ModalPortal } from '../dom/ModalPortal';

/**
 * // Class Obj: モーダルウィンドウの操作対象となる要素と表示切り替えを管理
 * // Note:
 * // - data-modal-* 属性を基準に、開閉ボタンや切替ボタンを収集します。
 * // - ここでは「どのモーダルを表示するか」の制御に集中します。
 * // - 汎用コンテンツの中身そのものは ModalContent 系のクラスで扱います。
 */
export class ModalControl
{
  private static readonly SELECTOR_MODAL = '[data-modal]';
  private static readonly SELECTOR_OPEN_BUTTON = '[data-modal-open]';
  private static readonly SELECTOR_CLOSE_BUTTON = '[data-modal-close]';
  private static readonly SELECTOR_PREV_BUTTON = '[data-modal-prev]';
  private static readonly SELECTOR_NEXT_BUTTON = '[data-modal-next]';
  private static readonly SELECTOR_PAGER_BUTTON = '[data-modal-pager]';
  private static readonly SELECTOR_CONTENT_NAV_BUTTON = '[data-modal-content-nav]';

  private static readonly ATTR_TARGET = 'data-modal-target';
  private static readonly ATTR_CONTENT_NAV = 'data-modal-content-nav';
  private static readonly ATTR_MODAL_STATE = 'data-modal-state';

  private readonly mModalPortal: ModalPortal;
  private readonly mFallbackRoot: HTMLElement;

  public readonly mModalRoot: HTMLElement | null;

  private readonly mAllModals: HTMLElement[];

  public readonly mOpenButtons: HTMLElement[];
  public readonly mCloseButtons: HTMLElement[];
  public readonly mNextButtons: HTMLElement[];
  public readonly mPrevButtons: HTMLElement[];
  /**
   * // Note: data-modal-pager は将来的なページャー拡張用として収集だけ残しています。
   */
  public readonly mPagerButtons: HTMLElement[];
  public readonly mContentNavButtons: HTMLElement[];

  /**
   * // Method: モーダル操作に使う要素を収集する
   * // Note:
   * // - modal_root が null の場合は空の root を使い、意図しない document 全体検索を避けます。
   */
  constructor(modal_root: HTMLElement | null, modal_portal: ModalPortal)
  {
    this.mModalPortal = modal_portal;
    this.mFallbackRoot = document.createElement('div');

    this.mModalRoot = modal_root;

    this.mOpenButtons = this.getElementsBySelector(ModalControl.SELECTOR_OPEN_BUTTON);
    this.mCloseButtons = this.getElementsBySelector(ModalControl.SELECTOR_CLOSE_BUTTON);
    this.mPrevButtons = this.getElementsBySelector(ModalControl.SELECTOR_PREV_BUTTON);
    this.mNextButtons = this.getElementsBySelector(ModalControl.SELECTOR_NEXT_BUTTON);
    this.mPagerButtons = this.getElementsBySelector(ModalControl.SELECTOR_PAGER_BUTTON);
    this.mContentNavButtons = this.getElementsBySelector(ModalControl.SELECTOR_CONTENT_NAV_BUTTON);

    this.mAllModals = this.getElementsBySelector(ModalControl.SELECTOR_MODAL);
  }

  /**
   * // Method: 指定したモーダルが表示中か判定する
   */
  public isActiveModal(modal: HTMLElement): boolean
  {
    return modal.getAttribute(ModalControl.ATTR_MODAL_STATE) === 'active';
  }

  /**
   * // Method: root 配下から指定セレクタに一致する要素を配列で取得する
   */
  private getElementsBySelector(selector: string): HTMLElement[]
  {
    const root: HTMLElement = this.mModalRoot ?? this.mFallbackRoot;
    const elements: HTMLElement[] = Array.from(root.querySelectorAll<HTMLElement>(selector));

    if (root.matches(selector))
    {
      elements.unshift(root);
    }

    return elements;
  }

  /**
   * // Method: data-modal-target に紐づくモーダル要素を取得する
   * // Note:
   * // - 値に # が含まれていても取得できるように補正します。
   */
  public getTargetModalByElement(element: HTMLElement): HTMLElement | null
  {
    const target_modal_id: string | null = element.getAttribute(ModalControl.ATTR_TARGET);

    if (!target_modal_id)
    {
      return null;
    }

    return document.getElementById(target_modal_id.replace(/^#/, ''));
  }

  /**
   * // Method: 要素に関連するモーダルを取得する
   * // Note:
   * // - モーダル内のボタンなら closest('[data-modal]') を優先します。
   * // - 開くボタンなどモーダル外の要素なら data-modal-target から取得します。
   */
  public getRelatedModalByElement(element: HTMLElement): HTMLElement | null
  {
    const current_modal: HTMLElement | null = element.closest<HTMLElement>(ModalControl.SELECTOR_MODAL);

    return current_modal ?? this.getTargetModalByElement(element);
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションの指定値を取得する
   * // Note:
   * // - data-modal-content-nav="prev" / "next" は推奨の抽象指定です。
   * // - next / prev は、ModalDisplay 側で現在 group の choose item 状態に応じた具体的な移動種別へ解決します。
   * // - 具体指定として、prev-item / next-item / prev-group / next-group も受け付けます。
   * // - prev-item / next-item は、ModalDisplay 側で choose item がない構成だけ group 移動へ補正できます。
   */
  public getContentNavigationActionByElement(element: HTMLElement): ModalContentNavigationAction | null
  {
    const navigation_action: string | null = element.getAttribute(ModalControl.ATTR_CONTENT_NAV);

    if (
      navigation_action === 'next' ||
      navigation_action === 'prev' ||
      navigation_action === 'next-item' ||
      navigation_action === 'prev-item' ||
      navigation_action === 'next-group' ||
      navigation_action === 'prev-group'
    )
    {
      return navigation_action;
    }

    return null;
  }

  /**
   * // Method: 指定したモーダルを表示する
   */
  private showModal(modal: HTMLElement): void
  {
    modal.setAttribute(ModalControl.ATTR_MODAL_STATE, 'active');
  }

  /**
   * // Method: 指定したモーダルを非表示にする
   */
  private hideModal(modal: HTMLElement): void
  {
    modal.removeAttribute(ModalControl.ATTR_MODAL_STATE);
  }

  /**
   * // Method: 管理対象の全モーダルを取得する
   */
  public getAllModals(): HTMLElement[]
  {
    return this.mAllModals;
  }

  /**
   * // Method: 管理対象の全モーダルを非表示にする
   */
  public hideAllModals(): void
  {
    this.mAllModals.forEach((modal: HTMLElement) =>
    {
      this.hideModal(modal);
    });
  }

  /**
   * // Method: data-modal-target に応じて表示するモーダルを切り替える
   * // Note:
   * // - 現在のモーダルを非表示にし、対象モーダルを表示します。
   * // - 対象モーダルは表示前に body 直下へ portal 化します。
   */
  public changeWindowByElement(element: HTMLElement): void
  {
    const current_modal: HTMLElement | null = element.closest<HTMLElement>(ModalControl.SELECTOR_MODAL);
    const target_modal: HTMLElement | null = this.getTargetModalByElement(element);

    if (target_modal)
    {
      this.mModalPortal.portal(target_modal);
    }

    if (current_modal)
    {
      this.hideModal(current_modal);
    }

    if (target_modal)
    {
      this.showModal(target_modal);
    }

    this.mAllModals.forEach((modal: HTMLElement) =>
    {
      if (modal !== current_modal && modal !== target_modal)
      {
        this.hideModal(modal);
      }
    });
  }

  /**
   * // Method: 指定モーダル内の汎用コンテンツ用ナビゲーションボタンを取得する
   */
  public getContentNavButtonsByModal(modal: HTMLElement | null): HTMLElement[]
  {
    if (!modal)
    {
      return [];
    }

    return Array.from(
      modal.querySelectorAll<HTMLElement>(ModalControl.SELECTOR_CONTENT_NAV_BUTTON),
    );
  }
}
